import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { UnknownOutputParams } from 'expo-router'
import { useCallback } from 'react'

import { useToast } from '@/src/general/components/ToastContext/ToastContext'
import { HTTPException } from '@/src/general/errors/HTTPException'
import type { TDateHyphenYYYYMMDD } from '@/src/general/primitives/dates/date.types'
import { dateToYYYYMMDD } from '@/src/general/primitives/dates/dayjs'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import { useTranslate } from '@/src/lang/useTranslate'

import { communityApi } from '../api/communityApi'
import { buildJoinPayload } from '../utils/buildJoinPayload'
import { invalidateSubscriptionQueries } from '../utils/invalidateSubscriptionQueries'
import { communityQueryOptions } from './useCommunityQuery'

const getTodayDate = (): TDateHyphenYYYYMMDD => dayjs().utc().add(2, 'hours').format('YYYY-MM-DD')

const getJoinParams = (when: 'ongoing' | 'today' | Date) => {
  if (when === 'ongoing') return null
  const today = getTodayDate()
  if (when === 'today') return today
  return dayjs().isAfter(when) ? today : dateToYYYYMMDD(when)
}
const shouldKickstart = (scheduleDate: TDateHyphenYYYYMMDD | null) =>
  scheduleDate === null || scheduleDate === getTodayDate()

export const useJoinCommunityMutation = (id: number, searchParams: UnknownOutputParams, onSuccess?: VoidFunction) => {
  const queryClient = useQueryClient()
  const { t } = useTranslate()
  const toast = useToast()

  const kickStartMutation = useMutation({
    mutationKey: ['kickStartCommunity', id],
    mutationFn: () => communityApi.kickstartSubscription(id),
  })

  const joinMutation = useMutation({
    mutationKey: ['joinCommunity', id],
    mutationFn: async (scheduleDate: TDateHyphenYYYYMMDD | null) => {
      try {
        return await communityApi.joinCommunity(id, { scheduleDate, ...buildJoinPayload(searchParams) })
      } catch (error) {
        if (error instanceof HTTPException && error.status === 409) {
          toast.addToast({ message: t('community.join.already-joined'), colors: 'secondary' })
          return null
        }
        throw error
      }
    },
    // Cancel any pending queries to avoid race conditions and then overriding the cache mutation
    onMutate: () => queryClient.cancelQueries(communityQueryOptions(id)),
    onSuccess: async (result, scheduleDate) => {
      const isAlreadyJoined = result === null
      const shouldRunKickstart = !isAlreadyJoined && shouldKickstart(scheduleDate)
      try {
        const communityInvalidation = queryClient.invalidateQueries(communityQueryOptions(id))
        const subscriptionInvalidation = invalidateSubscriptionQueries(queryClient, {
          id,
          lifetime: undefined,
          hasJoined: undefined,
        })
        let kickstartPromise: Promise<unknown> | undefined
        if (shouldRunKickstart) {
          kickstartPromise = kickStartMutation.mutateAsync()
        }
        await Promise.all([communityInvalidation, subscriptionInvalidation, kickstartPromise])
      } catch (_) {}
      onSuccess?.()
    },
    onError: (error) => {
      toast.addToast({ message: t(parseApiError(error, 'scheduleDate') || 'common.error.unknown'), colors: 'danger' })
    },
  })

  const { mutate } = joinMutation
  const join = useCallback((when: 'ongoing' | 'today' | Date) => mutate(getJoinParams(when)), [mutate])

  return {
    ...joinMutation,
    join,
  }
}
